import {
  ClarificationItem,
  GenerationConfig,
  GenerationMetrics,
  GenerationMode,
  GenerationProcessStage,
  GenerationProgressState,
  Message,
  PlanProcessSummary,
  PlcBrand,
  PlcLanguage,
  StageStreamChunk,
  ChatSessionConfigSnapshot,
  type TiaTargetResolvedChunk,
} from '../types';
import { KEPT_UNLESS_SENT_SESSION_CONFIG_FIELDS, SERVER_ONLY_SESSION_CONFIG_FIELDS } from '@realplc/types';
import { looksLikePlcCode, serverDeclaredNoProgram } from './messageTopic';
import type { TurnGenerationConfig } from './sessionTiaTarget';
import { extractCleanStructuredCode, stripMarkdownFence } from './structuredResponse';
import { readDeliveryReview } from './deliveryValidationLabel';

/**
 * Fold a metrics patch onto what the stream has established so far.
 *
 * An absent field means "this patch says nothing about it", never "erase it".
 * The worker publishes metadata several times per turn and each publish carries
 * only what it knew at the time, so spreading the undefined keys let a later,
 * thinner publish wipe out a `tiaValidation` or a `codesysValidation` an
 * earlier one had already established. Losing the CODESYS run id that way is
 * what sent the browser off to open a second validation Run for a compile the
 * worker had already finished — a second IDE launch to learn what the
 * diagnostics on screen already said.
 */
export function mergeGenerationMetrics(
  previous: GenerationMetrics | undefined,
  incoming: Partial<GenerationMetrics>,
): GenerationMetrics {
  const known = Object.fromEntries(
    Object.entries(incoming).filter(([, value]) => value !== undefined),
  ) as Partial<GenerationMetrics>;
  const merged = { ...(previous || {}), ...known };

  // A turn starts once, so its anchor only ever moves backwards.
  //
  // Seen live 2026-09-15: the running counter read 「已用 5分31秒」 and, without
  // anything restarting, 「已用 31秒」. A turn can be carried by more than one
  // job — a replay, a continuation — and each publishes its own acceptance
  // time. Taking the newest one re-anchored the clock to the newest job, so the
  // counter reported how long the last leg had been running while the user read
  // it as how long they had been waiting.
  const anchors = [previous?.gatewayAcceptedAt, known.gatewayAcceptedAt]
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (anchors.length > 0) merged.gatewayAcceptedAt = Math.min(...anchors);

  return merged;
}

/** A stream chunk's `metadata` as the worker sent it: nothing in it is checked yet. */
type StreamMetadata = { timings?: Record<string, unknown>; [key: string]: unknown };

/**
 * The generation metrics a stream chunk's `metadata` carries, each only when it
 * has the shape the page reads it as.
 *
 * Nothing else copies the stream's metadata onto the message, so a field left
 * off this list never reaches the page. `testHints` was left off (review,
 * 2026-09-29): the done frame said the reading of the test cases was pending,
 * the tab that ran the turn never heard it and so never polled for the result,
 * and a reload does not refetch a settled session, so the hints never showed.
 */
export function generationMetricsFromStreamMetadata(metadata: StreamMetadata): Partial<GenerationMetrics> {
  return {
    deliveryQuality: ['pending', 'passed', 'failed', 'incomplete'].includes(String(metadata.deliveryQuality))
      ? metadata.deliveryQuality as GenerationMetrics['deliveryQuality'] : undefined,
    physicalRequestStarts: typeof metadata.physicalRequestStarts === 'number' ? metadata.physicalRequestStarts : undefined,
    candidateAttemptRecords: typeof metadata.candidateAttemptRecords === 'number' ? metadata.candidateAttemptRecords : undefined,
    requestAccounting: metadata.requestAccounting && typeof metadata.requestAccounting === 'object'
      && (metadata.requestAccounting as { version?: unknown }).version === 1
      ? metadata.requestAccounting as GenerationMetrics['requestAccounting'] : undefined,
    requestStop: metadata.requestStop && typeof metadata.requestStop === 'object'
      && typeof (metadata.requestStop as { code?: unknown }).code === 'string'
      ? metadata.requestStop as GenerationMetrics['requestStop'] : undefined,
    reviewRequired: readDeliveryReview(metadata.reviewRequired),
    traceId: typeof metadata.traceId === 'string' ? metadata.traceId : undefined,
    requestId: typeof metadata.requestId === 'string' ? metadata.requestId : undefined,
    provider: typeof metadata.provider === 'string' ? metadata.provider : undefined,
    // Which toolchain this turn ran for, kept with the turn. The
    // toolbar chip beside it answers a different question: what is
    // selected now.
    targetBrand: typeof metadata.targetBrand === 'string' ? metadata.targetBrand : undefined,
    targetLanguage: typeof metadata.targetLanguage === 'string' ? metadata.targetLanguage : undefined,
    model: typeof metadata.model === 'string' ? metadata.model : undefined,
    gatewayAcceptedAt: typeof metadata.timings?.gatewayAcceptedAt === 'number' ? metadata.timings.gatewayAcceptedAt : undefined,
    queueEnqueuedAt: typeof metadata.timings?.queueEnqueuedAt === 'number' ? metadata.timings.queueEnqueuedAt : undefined,
    workerStartedAt: typeof metadata.timings?.workerStartedAt === 'number' ? metadata.timings.workerStartedAt : undefined,
    gatewaySetupMs: typeof metadata.timings?.gatewaySetupMs === 'number' ? metadata.timings.gatewaySetupMs : undefined,
    queueWaitMs: typeof metadata.timings?.queueWaitMs === 'number' ? metadata.timings.queueWaitMs : undefined,
    requestToWorkerStartMs: typeof metadata.timings?.requestToWorkerStartMs === 'number' ? metadata.timings.requestToWorkerStartMs : undefined,
    workerTotalMs: typeof metadata.timings?.workerTotalMs === 'number' ? metadata.timings.workerTotalMs : undefined,
    totalEndToEndMs: typeof metadata.timings?.totalEndToEndMs === 'number' ? metadata.timings.totalEndToEndMs : undefined,
    modelFirstTokenMs: typeof metadata.timings?.modelFirstTokenMs === 'number' ? metadata.timings.modelFirstTokenMs : undefined,
    modelLatencyMs: typeof metadata.timings?.modelLatencyMs === 'number' ? metadata.timings.modelLatencyMs : undefined,
    topicClassifyMs: typeof metadata.timings?.topicClassifyMs === 'number' ? metadata.timings.topicClassifyMs : undefined,
    validationMs: typeof metadata.timings?.validationMs === 'number' ? metadata.timings.validationMs : undefined,
    repairMs: typeof metadata.timings?.repairMs === 'number' ? metadata.timings.repairMs : undefined,
    modelAttempts: Array.isArray(metadata.attempts) ? metadata.attempts : undefined,
    tiaValidation: metadata.tiaValidation && typeof metadata.tiaValidation === 'object' && !Array.isArray(metadata.tiaValidation)
      ? metadata.tiaValidation as GenerationMetrics['tiaValidation']
      : undefined,
    contextMemory: metadata.contextMemory && typeof metadata.contextMemory === 'object'
      ? metadata.contextMemory as GenerationMetrics['contextMemory']
      : undefined,
    jianContextContract: metadata.contextContract && typeof metadata.contextContract === 'object' && !Array.isArray(metadata.contextContract)
      ? metadata.contextContract as GenerationMetrics['jianContextContract']
      : undefined,
    jianToolTrace: Array.isArray(metadata.jianToolTrace)
      ? metadata.jianToolTrace as GenerationMetrics['jianToolTrace']
      : undefined,
    vendorExecutionProfile: metadata.vendorExecutionProfile && typeof metadata.vendorExecutionProfile === 'object' && !Array.isArray(metadata.vendorExecutionProfile)
      ? metadata.vendorExecutionProfile as GenerationMetrics['vendorExecutionProfile']
      : undefined,
    routingDecision: metadata.routingDecision && typeof metadata.routingDecision === 'object' && !Array.isArray(metadata.routingDecision)
      ? metadata.routingDecision as GenerationMetrics['routingDecision']
      : undefined,
    artifactRegistry: metadata.artifactRegistry && typeof metadata.artifactRegistry === 'object' && !Array.isArray(metadata.artifactRegistry)
      ? metadata.artifactRegistry as GenerationMetrics['artifactRegistry']
      : undefined,
    // The CODESYS Run the worker already opened inside this turn.
    // Without it the browser cannot tell that the compile has already
    // happened, so it dispatches a second Run under its own
    // idempotency key and spends another IDE launch learning what the
    // diagnostics on screen already say.
    codesysValidation: metadata.codesysValidation && typeof metadata.codesysValidation === 'object' && !Array.isArray(metadata.codesysValidation)
      ? metadata.codesysValidation as GenerationMetrics['codesysValidation']
      : undefined,
    testHints: metadata.testHints && typeof metadata.testHints === 'object' && !Array.isArray(metadata.testHints)
      ? metadata.testHints as GenerationMetrics['testHints']
      : undefined,
  };
}

/**
 * The preflight's own report, written once and then left alone.
 *
 * The field is named for the preflight, but the write that fed it fired on
 * every `stage_artifact` the validation stage emitted — and the repair loop
 * re-runs validation once per round, each run emitting one. So the field held
 * whichever validation ran last, and the step reading it changed its numbers
 * under the user every round.
 *
 * 2026-09-15 19:42 线上：预检那一行跑的时候写「8 类根因（共 20 处）」，编译段
 * 开始后变成「6 类根因（共 17 处）」，而它上方由运行时封存的标题一直写着
 * 「未通过：3 个错误、17 个警告」——3 + 17 = 20。标题封存了，数字没有。
 *
 * 第一份产物是预检的那一份，后面每一份都是某一轮修复之后的复检。先到的那份
 * 胜出，别的只是同一个阶段又跑了一次。
 */
export function preflightReportPatch(
  existing: unknown,
  artifact: unknown,
): { preflightReport?: unknown } {
  if (existing !== undefined && existing !== null) return {};
  if (artifact === undefined || artifact === null) return {};
  return { preflightReport: artifact };
}

export type PlanProcessHistory = NonNullable<Message['planProcessHistory']>;
export type PlanProcessHistoryItem = PlanProcessHistory[number];
export type PlanReviewResult = NonNullable<NonNullable<Message['planReview']>['reviewResult']>;
export type PlanReviewApprovalSource = NonNullable<NonNullable<Message['planReview']>['approvalSource']>;

/**
 * engineeringReasoningId is emitted by the backend in SSE chunk metadata under
 * chunk.metadata.engineeringReasoning.engineeringReasoningId  and is used for
 * causal chain validation (see PipelinePlanBasedOn). It flows through to
 * message.metadata?.engineeringReasoning?.engineeringReasoningId  at display
 * time. Extract it during SSE chunk processing:
 *
 *   const engineeringReasoningId =
 *     (chunk.metadata as any)?.engineeringReasoning?.engineeringReasoningId
 *     || undefined;
 */
export type EngineeringReasoningId = string;

export const STREAM_PERSIST_THROTTLE_MS = 1000;
export const STREAM_PARSE_THROTTLE_MS = 360;
export const STREAM_RENDER_THROTTLE_MS = 100;
export const STREAM_SESSION_STATE_THROTTLE_MS = 1600;

export function planReviewSignature(planReview: unknown): string {
  if (!planReview || typeof planReview !== 'object') return '';
  const plan = planReview as NonNullable<Message['planReview']>;
  const subtaskIds = (plan.subtasks || []).map(item => `${item.id}:${item.title}`).join('|');
  const subtaskDetails = (plan.subtasks || []).map(item => `${item.description}:${item.acceptanceCriteria?.join(',')}`).join('|');
  return [
    plan.channel,
    plan.requirementSummary,
    plan.architecture?.programStructure,
    subtaskIds,
    subtaskDetails,
    plan.selfCheck?.coverageScore,
    plan.selfCheck?.safetyCoverage,
  ].filter(Boolean).join('::');
}

export function validatePlanReview(data: unknown, current: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;

  const incoming = data as Record<string, unknown>;
  const merged: Record<string, unknown> = { ...(current && typeof current === 'object' ? current as Record<string, unknown> : {}), ...incoming };
  const currentSignature = planReviewSignature(current);
  const incomingSignature = planReviewSignature(merged);
  if (
    currentSignature
    && incomingSignature
    && currentSignature !== incomingSignature
    && incoming.reviewResult === undefined
  ) {
    delete merged.reviewResult;
  }

  if (merged.subtasks !== undefined && merged.subtasks !== null) {
    if (!Array.isArray(merged.subtasks)) {
      console.warn('[stream-state] planReview.subtasks is not an array, discarding', merged.subtasks);
      merged.subtasks = [];
    } else {
      merged.subtasks = merged.subtasks.filter((st: unknown) => st && typeof st === 'object')
        .map((st: unknown) => {
          const s = st as Record<string, unknown>;
          return {
            ...s,
            id: typeof s.id === 'string' ? s.id : '',
            title: typeof s.title === 'string' ? s.title : '',
            category: typeof s.category === 'string' ? s.category : 'core_logic',
            description: typeof s.description === 'string' ? s.description : '',
            dependencies: Array.isArray(s.dependencies) ? s.dependencies : [],
            acceptanceCriteria: Array.isArray(s.acceptanceCriteria) ? s.acceptanceCriteria : [],
            estimatedComplexity: typeof s.estimatedComplexity === 'string' ? s.estimatedComplexity : 'moderate',
          };
        });
    }
  }

  if (merged.risks !== undefined && merged.risks !== null && !Array.isArray(merged.risks)) {
    console.warn('[stream-state] planReview.risks is not an array, discarding', merged.risks);
    merged.risks = [];
  }

  if (merged.selfCheck && typeof merged.selfCheck === 'object') {
    const sc = merged.selfCheck as Record<string, unknown>;
    if (typeof sc.coverageScore !== 'number') sc.coverageScore = Number(sc.coverageScore) || 0;
    if (typeof sc.safetyCoverage !== 'number') sc.safetyCoverage = Number(sc.safetyCoverage) || 0;
    if (typeof sc.passed !== 'boolean') sc.passed = false;
    if (!Array.isArray(sc.issues)) sc.issues = [];
  }

  if (merged.requirementSummary !== undefined && typeof merged.requirementSummary !== 'string') {
    merged.requirementSummary = String(merged.requirementSummary);
  }
  if (merged.requirementTableMarkdown !== undefined && typeof merged.requirementTableMarkdown !== 'string') {
    merged.requirementTableMarkdown = String(merged.requirementTableMarkdown);
  }

  return merged;
}

const PROCESS_STAGES: GenerationProcessStage[] = [
  'routing',
  'requirement_analysis',
  'clarification',
  'planning',
  'generation',
  'validation',
  'repair',
  // Will be published by the worker right after the repair stage ends
  // (TA10a). Missing from this whitelist, `normalizeProcessStage` rejected it
  // as unrecognised and `processStageFromProgressState` fell back to
  // guessing a stage from `phase: 'finalizing'` instead — which resolves to
  // 'generation' below, silently overwriting the one signal TA10b's
  // `isRepairStageDone` reads.
  'final_checks',
  'completed',
  'failed',
];

export function normalizeProcessStage(value: unknown): GenerationProcessStage | undefined {
  return typeof value === 'string' && PROCESS_STAGES.includes(value as GenerationProcessStage)
    ? value as GenerationProcessStage
    : undefined;
}

export function processStageFromProgressState(progressState?: GenerationProgressState): GenerationProcessStage | undefined {
  const explicit = normalizeProcessStage(progressState?.stage);
  if (explicit) return explicit;

  switch (progressState?.phase) {
    case 'queued':
    case 'dispatching':
    case 'classifying':
      return 'routing';
    case 'planning':
      return progressState.planPhase === 'analyzing' || progressState.planPhase === 'classifying'
        ? 'requirement_analysis'
        : 'planning';
    case 'clarifying':
      return 'clarification';
    case 'generating':
    case 'explaining':
    case 'finalizing':
      return 'generation';
    case 'validating':
      return 'validation';
    case 'repairing':
      return 'repair';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    default:
      return undefined;
  }
}

/**
 * Whether a `planning`/`analyzing` progress frame is reporting a replan, not
 * the turn's first pass through requirement analysis.
 *
 * Read from the runtime's own structured `replanAttempt` counter (1..3), not
 * from `label` text: the worker's Chinese replanning label — 「方案被退回，正在
 * 重新规划（n/3）…」 — never matched the `/re-?planning|rejected/i` regex this
 * replaced, so a Chinese turn's plan history never opened its own 「重新规划中」
 * item. The worker sets `replanAttempt` on the frame that starts each re-plan
 * (`GenerationProgressState`, packages/types).
 */
export function isReplanningProgressState(progressState: GenerationProgressState | undefined): boolean {
  if (!progressState) return false;
  const replanAttempt = progressState.replanAttempt ?? 0;
  return progressState.phase === 'planning' && progressState.planPhase === 'analyzing' && replanAttempt > 0;
}

function createPlanProcessHistoryItem(
  sequence: number,
  source: PlanProcessSummary['source'] = 'generated',
  reasoning = '',
  stage: GenerationProcessStage = 'planning',
): PlanProcessHistoryItem {
  const now = Date.now();
  return {
    id: `plan-process-${sequence}`,
    sequence,
    source,
    stage,
    status: 'streaming',
    reasoning,
    startedAt: now,
    updatedAt: now,
  };
}

function ensurePlanProcessHistoryItem(
  history: PlanProcessHistory,
  source: PlanProcessSummary['source'] = 'generated',
  reasoning = '',
  stage: GenerationProcessStage = 'planning',
): PlanProcessHistory {
  if (history.length === 0) {
    return [createPlanProcessHistoryItem(1, source, reasoning, stage)];
  }
  return history;
}

export function startPlanProcessHistoryItem(
  history: PlanProcessHistory,
  source: PlanProcessSummary['source'],
  reasoning: string,
  stage: GenerationProcessStage = 'planning',
): PlanProcessHistory {
  const latest = history[history.length - 1];
  if (latest?.status === 'streaming' && latest.source === source && !latest.planReview) {
    return history.map((item, index) =>
      index === history.length - 1
        ? { ...item, reasoning: item.reasoning || reasoning, stage: item.stage || stage, updatedAt: Date.now() }
        : item,
    );
  }

  return [
    ...history,
    createPlanProcessHistoryItem((latest?.sequence || history.length) + 1, source, reasoning, stage),
  ];
}

export function appendPlanProcessReasoning(
  history: PlanProcessHistory,
  text: string,
  stage: GenerationProcessStage = 'planning',
): PlanProcessHistory {
  if (!text) return history;
  const latest = history[history.length - 1];
  if (latest?.reasoning && latest.stage && latest.stage !== stage) {
    return [
      ...history,
      createPlanProcessHistoryItem((latest.sequence || history.length) + 1, 'generated', text, stage),
    ];
  }

  const ensured = ensurePlanProcessHistoryItem(history, 'generated', '', stage);
  return ensured.map((item, index) =>
    index === ensured.length - 1
      ? { ...item, stage: item.stage || stage, reasoning: `${item.reasoning || ''}${text}`, updatedAt: Date.now() }
      : item,
  );
}

export function applyPlanProcessSummary(history: PlanProcessHistory, summary: PlanProcessSummary): PlanProcessHistory {
  const stage: GenerationProcessStage = summary.outputType === 'clarify' ? 'clarification' : 'planning';
  const ensured = ensurePlanProcessHistoryItem(history, summary.source, '', stage);
  const latest = ensured[ensured.length - 1];
  const shouldAppend = Boolean(
    latest
      && latest.summary
      && latest.planReview
      && summary.source !== latest.source,
  );
  const target = shouldAppend
    ? [...ensured, createPlanProcessHistoryItem((latest?.sequence || ensured.length) + 1, summary.source, '', stage)]
    : ensured;

  return target.map((item, index) =>
    index === target.length - 1
      ? {
          ...item,
          source: summary.source,
          stage: item.stage || stage,
          summary,
          status: item.planReview ? 'awaiting_review' : item.status,
          updatedAt: Date.now(),
        }
      : item,
  );
}

export function applyPlanReviewToHistory(history: PlanProcessHistory, planReview: NonNullable<Message['planReview']>): PlanProcessHistory {
  const ensured = ensurePlanProcessHistoryItem(history, 'generated', '', 'planning');
  const latest = ensured[ensured.length - 1];
  const incomingSignature = planReviewSignature(planReview);
  const shouldAppend = Boolean(
    latest?.planReview
      && planReviewSignature(latest.planReview) !== incomingSignature
      && latest.planReview.reviewResult,
  );
  const target = shouldAppend
    ? [...ensured, createPlanProcessHistoryItem((latest?.sequence || ensured.length) + 1, 'replanned', '', 'planning')]
    : ensured;

  return target.map((item, index) =>
    index === target.length - 1
      ? {
          ...item,
          stage: item.stage || 'planning',
          planReview,
          status: planReview.reviewResult ? 'submitted' : 'awaiting_review',
          updatedAt: Date.now(),
        }
      : item,
  );
}

/**
 * Whether a decision on a plan keeps the proposed signals ticked with it: a
 * decision made by hand (an approval by hand, or an edited plan submitted) on
 * a plan that proposes any. Never the countdown's approval, which accepts
 * none. Every place that stores a decision asks here.
 */
export function keepsTickedSignals(
  plan: Pick<NonNullable<Message['planReview']>, 'approvalSource' | 'proposedSignals'>,
  action: PlanReviewResult,
  approvalSource: PlanReviewApprovalSource | undefined,
  acceptedSignals: string[] | undefined,
): acceptedSignals is string[] {
  const source = approvalSource || plan.approvalSource;
  return Array.isArray(acceptedSignals)
    && (action === 'modified' || (action === 'approved' && source === 'manual'))
    && Array.isArray(plan.proposedSignals) && plan.proposedSignals.length > 0;
}

export function applyPlanReviewResultToHistory(
  history: PlanProcessHistory | undefined,
  action: PlanReviewResult,
  approvalSource?: PlanReviewApprovalSource,
  /**
   * The proposed signals the engineer ticked. This entry is the copy of the
   * plan a card is drawn from, so they are kept on a plan decided by hand,
   * for a card drawn again from it before the worker republishes the plan.
   * Never kept for the countdown's approval, which accepts none.
   */
  acceptedSignals?: string[],
): PlanProcessHistory | undefined {
  if (!history?.length) return history;
  let changed = false;
  const next = [...history];

  for (let index = next.length - 1; index >= 0; index--) {
    const item = next[index];
    if (!item.planReview || item.planReview.reviewResult) continue;
    changed = true;
    next[index] = {
      ...item,
      status: 'submitted',
      updatedAt: Date.now(),
      planReview: {
        ...item.planReview,
        reviewResult: action,
        approvalSource: approvalSource || item.planReview.approvalSource,
        ...(keepsTickedSignals(item.planReview, action, approvalSource, acceptedSignals) ? { acceptedSignals } : {}),
      },
    };
    break;
  }

  return changed ? next : history;
}

function clarifyItemKey(item: ClarificationItem): string {
  const fieldKey = item.fieldKeys?.length
    ? item.fieldKeys.map(key => key.trim()).filter(Boolean).sort().join('|')
    : '';
  if (fieldKey) return `fields:${fieldKey}`;
  return `${item.category}:${item.question.trim().toLowerCase().replace(/\s+/g, '')}`;
}

export function dedupeClarificationItems(items: ClarificationItem[] = []): ClarificationItem[] {
  const seen = new Set<string>();
  const deduped: ClarificationItem[] = [];

  for (const item of items) {
    if (!item?.question?.trim()) continue;
    const key = clarifyItemKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(item);
  }

  return deduped;
}

/**
 * Fills in the optional parts of a generation config. The target platform is
 * not one of them.
 *
 * `brand` and `language` used to fall back to Siemens/ST, which made a call site
 * that forgot to pass them indistinguishable from a user who really chose
 * Siemens. On 2026-09-07 that turned a CODESYS session into Siemens/SCL the
 * moment the user pressed 继续执行 — handleResumePlan passed only
 * `{ preApprovedPlan }`, the rest defaulted, the CODESYS compile gate was
 * skipped for the whole round ("本轮目标不是 CODESYS"), and the artifact landed
 * as .scl. `model` defaulted the same way and relabelled the finished turn
 * AutoFree.
 *
 * The platform has exactly two legitimate sources: what the user has selected
 * now, or the session's own snapshot. Anything else is a bug, and it should
 * surface here rather than downstream as a program in the wrong language.
 */
export function normalizeGenerationConfig(config?: Partial<GenerationConfig>): GenerationConfig {
  if (typeof config?.brand !== 'string' || !config.brand.trim()) {
    throw new Error('normalizeGenerationConfig: brand is required — pass the session config, not a partial');
  }
  if (typeof config?.language !== 'string' || !config.language.trim()) {
    throw new Error('normalizeGenerationConfig: language is required — pass the session config, not a partial');
  }
  return {
    ...config,
    brand: config.brand as PlcBrand,
    language: config.language as PlcLanguage,
    context: config?.context || '',
    projectId: config?.projectId,
    includeComments: config?.includeComments ?? true,
    addSafetyRoutines: config?.addSafetyRoutines ?? false,
    strictSafetyCheck: config?.strictSafetyCheck ?? true,
    customInstructions: config?.customInstructions || '',
    model: config?.model || 'autofree',
    useWebSearch: config?.useWebSearch ?? false,
    providerApiKeys: config?.providerApiKeys || {},
    providerBaseUrls: config?.providerBaseUrls || {},
    uiLanguage: config?.uiLanguage || 'English',
    routingMode: config?.routingMode || GenerationMode.FAST,
    attachmentIds: Array.isArray(config?.attachmentIds) ? config.attachmentIds : [],
    attachments: Array.isArray(config?.attachments) ? config.attachments : [],
  } satisfies GenerationConfig;
}

/**
 * Whether what the turn delivered was cut off at the output limit.
 *
 * Of the attempts that delivered something. A reply that ran out of room with
 * nothing written is a failed attempt, and the next model is asked; its
 * `length` says nothing about the answer that model went on to give.
 */
export function hasTruncatedModelAttempt(metrics?: GenerationMetrics): boolean {
  return Boolean(metrics?.modelAttempts?.some((attempt) =>
    attempt.status !== 'error'
    && (attempt.truncated === true || attempt.finishReason?.toLowerCase() === 'length')
  ));
}

/**
 * The session snapshot the browser keeps for a turn it starts: this turn's
 * settings over `previous`, the way the server merges them (auth-core
 * `mergeWebSessionConfig`, TIA 根治 P2 T2).
 *
 * The web's settings replace the previous ones as a group, the CPU model among
 * them as `settingsPlcModel`. Kept from `previous`: the CPU model the server
 * resolved, with its source, while the platform stays the same, since it was
 * resolved for that platform's hardware; and the project reference and the
 * work mode, unless the turn carries them. Written whole, the turn's own
 * snapshot erased what the server had written until the next reload.
 */
export function createSessionConfigSnapshot(
  config: TurnGenerationConfig,
  previous?: ChatSessionConfigSnapshot,
): ChatSessionConfigSnapshot {
  const routingMode = config.routingMode === GenerationMode.THINKING ? GenerationMode.THINKING : GenerationMode.FAST;
  const targetProfile = config.targetProfile || {
    brand: config.brand,
    softwareVersion: config.softwareVersion,
    hardwareSeries: config.hardwareSeries,
    plcModel: config.settingsPlcModel,
    firmwareVersion: config.firmwareVersion,
  };
  const snapshot: ChatSessionConfigSnapshot = {
    brand: config.brand,
    language: config.language,
    projectId: config.projectId,
    preferredProvider: config.preferredProvider,
    preferredModel: config.preferredModel || config.model,
    routingMode,
    targetProfile,
    softwareVersion: config.softwareVersion,
    hardwareSeries: config.hardwareSeries,
    settingsPlcModel: config.settingsPlcModel,
    firmwareVersion: config.firmwareVersion,
  };
  // The field lists are @realplc/types', which auth-core's merge copies; both
  // merges run SESSION_CONFIG_MERGE_CASES.
  const turn = config as Record<string, unknown>;
  const merged = snapshot as unknown as Record<string, unknown>;
  if (previous?.plcModelSource !== undefined && previous.brand === config.brand) {
    for (const field of SERVER_ONLY_SESSION_CONFIG_FIELDS) merged[field] = previous[field];
  }
  for (const field of KEPT_UNLESS_SENT_SESSION_CONFIG_FIELDS) {
    const value = turn[field] ?? previous?.[field];
    if (value !== undefined) merged[field] = value;
  }
  return snapshot;
}

export function buildClientProgressState(
  language: string,
  phase: string,
  labelZh: string,
  labelEn: string,
  progress: number,
): GenerationProgressState {
  return {
    phase,
    stage: processStageFromProgressState({ phase }),
    label: language === 'English' ? labelEn : labelZh,
    progress,
    updatedAt: Date.now(),
  };
}

// The stage-stream reducer is shared with the runtime, which folds the same
// chunks so the record can be persisted. Re-exported here so existing callers
// keep their import path.
import { applyStageChunkToBuffers } from '@realplc/types';

export type { StageBufferKey } from '@realplc/types';
export {
  applyStageChunkToBuffers,
  isStageStreamChunk,
  mapStageToBufferKey,
  stageBufferPatch,
} from '@realplc/types';

export function applyStageChunk(message: Message, chunk: StageStreamChunk): Message {
  return {
    ...message,
    stageBuffers: applyStageChunkToBuffers(message.stageBuffers, chunk),
  };
}

/**
 * The parsed result a finished turn keeps.
 *
 * Two sticky sources feed `parsed.code` while a turn streams — the last parse
 * and the last code seen in the stage buffer — and both exist so that a program
 * is never lost to a later, emptier chunk. That is right for a program and
 * wrong for a parse error: when the server ends the stage without one, what
 * those sources are holding is whatever the browser mistook for code. On
 * 2026-09-20 that was 1204 characters of reasoning prose, kept as the turn's
 * program after the server had replaced the body with "no usable PLC code".
 */
export function finalParsedSnapshot<T extends { code?: string }>(
  parsed: T | undefined,
  stageBuffers: Message['stageBuffers'] | undefined,
): T | undefined {
  if (!parsed || !serverDeclaredNoProgram({ stageBuffers })) return parsed;
  return { ...parsed, code: '' };
}

/** Fields whose name says the value is code. Anything else is text that may contain some. */
const STAGE_ARTIFACT_CODE_KEYS = new Set(['finalCode', 'code', 'stCode', 'sclCode']);

export function extractCodeFromStageArtifact(artifact: unknown): string {
  const candidates: Array<{ text: string; declaredCode: boolean }> = [];

  if (typeof artifact === 'string') {
    candidates.push({ text: artifact, declaredCode: false });
  } else if (artifact && typeof artifact === 'object' && !Array.isArray(artifact)) {
    const record = artifact as Record<string, unknown>;
    for (const key of ['finalCode', 'code', 'stCode', 'sclCode', 'content', 'text']) {
      const value = record[key];
      if (typeof value === 'string') {
        candidates.push({ text: value, declaredCode: STAGE_ARTIFACT_CODE_KEYS.has(key) });
      }
    }
  }

  for (const candidate of candidates) {
    // A section, a fence, or a text that is mostly code: `extractCleanStructuredCode`
    // knows all three. What it cannot lift out is only offered whole when the
    // server named the field as code. Free text — the stage's accumulated
    // stream is one — used to be offered whole as well, and a single line
    // shaped like a statement then made the reasoning around it the program.
    const lifted = extractCleanStructuredCode(candidate.text);
    const code = stripMarkdownFence((lifted || (candidate.declaredCode ? candidate.text : '')).trim());
    if (looksLikePlcCode(code)) {
      return code;
    }
  }

  return '';
}

// =============================================================================
// V1 P0: TIA Tool Call & Workflow Event State
// =============================================================================

export type ToolCallStatus = 'running' | 'completed' | 'failed' | 'waiting';

export interface ToolCallState {
  id: string;
  name: string;
  label?: string;
  status: ToolCallStatus;
  startedAt: number;
  endedAt?: number;
  durationMs?: number;
  progressMessage?: string;
  progress?: number;
  result?: Record<string, unknown>;
  error?: string;
}

export { type WorkflowWaitingState } from '@realplc/types';

/** Check if a chunk is a tool_call event */
export function isToolCallChunk(chunk: unknown): chunk is {
  type: 'tool_call_start' | 'tool_progress' | 'tool_result' | 'tool_error';
  toolCallId: string;
  toolName: string;
} {
  if (!chunk || typeof chunk !== 'object' || Array.isArray(chunk)) return false;
  const r = chunk as Record<string, unknown>;
  return typeof r.type === 'string'
    && ['tool_call_start', 'tool_progress', 'tool_result', 'tool_error'].includes(r.type as string)
    && typeof r.toolCallId === 'string';
}

/** Check if a chunk is a workflow_waiting event */
export function isWorkflowWaitingChunk(chunk: unknown): chunk is {
  type: 'workflow_waiting';
  workflowRunId: string;
  stage: string;
  stageStatus: string;
  message: string;
} {
  if (!chunk || typeof chunk !== 'object' || Array.isArray(chunk)) return false;
  const r = chunk as Record<string, unknown>;
  return r.type === 'workflow_waiting';
}

/** Check if a chunk is a workflow_resumed event */
export function isWorkflowResumedChunk(chunk: unknown): chunk is {
  type: 'workflow_resumed';
  workflowRunId: string;
  stage: string;
} {
  if (!chunk || typeof chunk !== 'object' || Array.isArray(chunk)) return false;
  const r = chunk as Record<string, unknown>;
  return r.type === 'workflow_resumed';
}

/**
 * Check if a chunk says the server wrote the session's TIA target into its
 * snapshot (TIA 根治 P2): the resolved CPU model, or the project reference.
 * Its fields come from outside, so the merge still reads each one through
 * the types package's readers (mergeServerTiaTarget).
 */
export function isTiaTargetResolvedChunk(chunk: unknown): chunk is TiaTargetResolvedChunk {
  if (!chunk || typeof chunk !== 'object' || Array.isArray(chunk)) return false;
  return (chunk as Record<string, unknown>).type === 'tia_target_resolved';
}

/** Apply a tool_call_start chunk to the tool calls array */
export function applyToolCallStart(
  toolCalls: ToolCallState[] | undefined,
  chunk: { toolCallId: string; toolName: string; toolLabel?: string; timestamp?: number },
): ToolCallState[] {
  const existing = toolCalls ?? [];
  if (existing.some(tc => tc.id === chunk.toolCallId)) return existing;
  return [...existing, {
    id: chunk.toolCallId,
    name: chunk.toolName,
    label: chunk.toolLabel ?? chunk.toolName,
    status: 'running',
    startedAt: chunk.timestamp ?? Date.now(),
  }];
}

/** Apply a tool_progress chunk */
export function applyToolProgress(
  toolCalls: ToolCallState[] | undefined,
  chunk: { toolCallId: string; message?: string; progress?: number },
): ToolCallState[] {
  return (toolCalls ?? []).map(tc =>
    tc.id === chunk.toolCallId
      ? { ...tc, progressMessage: chunk.message ?? tc.progressMessage, progress: chunk.progress ?? tc.progress }
      : tc,
  );
}

/** Apply a tool_result chunk */
export function applyToolResult(
  toolCalls: ToolCallState[] | undefined,
  chunk: { toolCallId: string; ok: boolean; result?: Record<string, unknown>; durationMs?: number; timestamp?: number },
): ToolCallState[] {
  return (toolCalls ?? []).map(tc =>
    tc.id === chunk.toolCallId
      ? {
          ...tc,
          status: chunk.ok ? 'completed' as const : 'failed' as const,
          result: chunk.result,
          durationMs: chunk.durationMs,
          endedAt: chunk.timestamp ?? Date.now(),
        }
      : tc,
  );
}

/** Apply a tool_error chunk */
export function applyToolError(
  toolCalls: ToolCallState[] | undefined,
  chunk: { toolCallId: string; error?: string; durationMs?: number; timestamp?: number },
): ToolCallState[] {
  return (toolCalls ?? []).map(tc =>
    tc.id === chunk.toolCallId
      ? {
          ...tc,
          status: 'failed' as const,
          error: chunk.error ?? 'Unknown error',
          durationMs: chunk.durationMs,
          endedAt: chunk.timestamp ?? Date.now(),
        }
      : tc,
  );
}
